/* eslint-disable no-console, arrow-body-style, no-case-declarations */
import moment from 'moment';
import { reduce, camelCase } from 'lodash';
import * as signalRClient from '@/services/notifications/signal-r';
import * as firebaseClient from '@/services/notifications/firebase';
import store from '@/store';
import ls from '@/helpers/localStorage';
import log from '@/logger';
// import ss from '@/helpers/sessionStorage';
import { MODALS } from '@/components/app/app-modals/modals-constants';
import { FINISHED_STATUS, STEPS } from '@/components/wallet/add-funds-bank-transfer/add-funds-bank-transfer-constants';
import {
  NOTIFICATION_TRADE_CONFIRMED,
  NOTIFICATION_SIMPLE,
  NOTIFICATION_ACCOUNT_FUNDS,
  NOTIFICATION_MARKET,
  NOTIFICATION_MARKET_STATUS,
  NOTIFICATION_CONTRACT,
  NOTIFICATION_MARKET_OWNERSHIP,
  NOTIFICATION_CONTRACT_OWNERSHIP,
  NOTIFICATION_SHARES_TRADED,
  NOTIFICATION_SITE_STATUS,
  NOTIFICATION_TRADER_LEVEL_CHANGED,
  NOTIFICATION_PWMB_DEPOSIT_FINISHED,
  NOTIFICATION_PWMB_DEPOSIT_SHOW_CONFIRMATION,
  NOTIFICATION_PWMB_SHOW_ERROR,
} from '@/services/notifications/notification-types';

/*
  Notification type names moved to ./notification-types.js (import-free module)
  so consumers like utils/helpers.js don't need to import this proxy;
  re-exported here for backward compatibility.
 */
export * from '@/services/notifications/notification-types';

/*
  List of notification types that *should* display in top right corner of the app.
 */
export const ALLOWED_NOTIFICATION_TYPES = [
  NOTIFICATION_SIMPLE,
  NOTIFICATION_TRADE_CONFIRMED,
  NOTIFICATION_SHARES_TRADED,
  NOTIFICATION_TRADER_LEVEL_CHANGED,
];

export const IGNORE_TIMESTAMP_TYPES = [
  NOTIFICATION_TRADE_CONFIRMED,
  NOTIFICATION_SHARES_TRADED,
  NOTIFICATION_MARKET_STATUS,
];
/*
  Generic notification types that come from API (backend).
  The reason for separate list is determined by other typing mechanism used by
  API as opposed to signalr notifications.
  For now NOTIFICATION_SHARES_TRADED maps to NOTIFICATION_MATCHED.
  and NOTIFICATION_TRADER_LEVEL_CHANGED to NOTIFICATION_LEVEL
 */
export const NOTIFICATION_MATCHED = 1;
export const NOTIFICATION_SETTLED = 2;
export const NOTIFICATION_PRICE_CHANGE = 3;
export const NOTIFICATION_LEVEL = 12;
export const NOTIFICATIONS_ONLINE_BANKING = [13, 14];
/*
  Number of milliseconds to delay confirmations so they do not appear instantly after submit click
  because simple notification should display first.
 */
export const NOTIFICATION_TRADE_CONFIRMED_DELAY = 1250;
export const NOTIFICATION_TRADE_CONFIRMED_CLOSE_DELAY = 10000;
export const NOTIFICATION_SHARES_TRADED_CLOSE_DELAY = 5000;

export const MARKET_DATA_MAP = {
  TotalSharesTraded: 'totalSharesTraded',
  UserGainLoss: 'userGainLoss',
  UserInvestment: 'userInvestment',
  UserMaxPayout: 'userMaxPayout',
  Status: 'status',
  IsOpen: 'isOpen',
  IsActive: 'isActive',
  IsProcessed: 'isProcessed',
  UserHasOwnership: 'userHasOwnership',
};

// additional map because of TotalSharesTraded different mapping
export const MY_SHARES_MARKET_DATA_MAP = {
  TotalSharesTraded: 'marketSharesTraded',
  UserGainLoss: 'userGainLoss',
  UserInvestment: 'userInvestment',
  UserMaxPayout: 'userMaxPayout',
  Status: 'status',
  IsOpen: 'isOpen',
  IsActive: 'isActive',
  IsProcessed: 'isProcessed',
  IsOpenStatusMessage: 'isOpenStatusMessage',
  UserHasOwnership: 'userHasOwnership',
};

export const CONTRACT_DATA_MAP = {
  TimeStamp: 'timestamp',
  BestNoPrice: 'bestNoPrice',
  BestNoCost: 'bestNoPrice',
  BestYesPrice: 'bestYesPrice',
  BestYesCost: 'bestYesPrice',
  LastClosePrice: 'lastClosePrice',
  LastTradePrice: 'lastTradePrice',
  UserAveragePricePerShare: 'userAveragePricePerShare',
  UserQuantity: 'userQuantity',
  UserOpenOrdersBuyQuantity: 'userOpenOrdersBuyQuantity',
  UserOpenOrdersSellQuantity: 'userOpenOrdersSellQuantity',
  UserPrediction: 'userPrediction',
};

export const ACCOUNT_FUNDS_MAP = {
  AccountBalance: 'accountBalance',
  AccountBalanceDecimal: 'accountBalanceDecimal',
  PortfolioBalance: 'portfolioBalance',
  PortfolioBalanceDecimal: 'portfolioBalanceDecimal',
  ShareValueDelta: 'shareValueDelta',
  ShareValueDeltaDecimal: 'shareValueDeltaDecimal',
  ProfitablePredictions: 'profitablePredictions',
};

const pageLoad = moment();
const processedGuids = new Set();

const updateOrResetCounters = () => {
  store.dispatch('dashboard/toggleNotifications');

  if (!store.state.notifications.showNotificationsWidget) {
    store.dispatch('user/increaseUserUnreadNotificationsCount');
  } else {
    store.dispatch('user/setUserUnreadNotificationsCount', 0);
    store.dispatch('dashboard/markNotificationsAsRead');
  }
};


const addToStore = (method, updateType, data) => {
  if (!data) return;

  if (data && data.TimeStamp && !IGNORE_TIMESTAMP_TYPES.includes(updateType)) {
    if (moment.unix(data.TimeStamp).diff(pageLoad, 'seconds') < 0) {
      return;
    }
  }

  if (data.Guid) {
    if (processedGuids.has(data.Guid)) return;
    processedGuids.add(data.Guid);
  }

  data.UpdateType = updateType;
  switch (updateType) {
    case NOTIFICATION_ACCOUNT_FUNDS:
      store.dispatch('user/getStatus');
      store.dispatch('wallet/updateBalanceFromNotification', data);
      store.dispatch('profile/updateProfileFromNotification', data);
      break;
    case NOTIFICATION_MARKET:
    case NOTIFICATION_MARKET_OWNERSHIP:
    case NOTIFICATION_MARKET_STATUS:
      store.dispatch('market/updateMarketFromNotification', data);
      store.dispatch('market/updateRelatedMarketFromNotification', data);
      store.dispatch('browse/updateMarketFromNotification', data);
      // store.dispatch('browse/updateStoryMapFromMarketNotification', data);
      // store.dispatch('browse/updateGainsLossMarketFromNotification', data);
      store.dispatch('browse/updatePredictedTrendingMarketFromNotification', data);
      store.dispatch('dashboard/updateMySharesFromNotification', data);
      store.dispatch('search/updateMarketFromNotification', data);
      store.dispatch('watchlist/updateWatchlistFromNotification', data);
      store.dispatch('popup/updateTradePopupOptionsMarketFromNotification', data);
      break;
    case NOTIFICATION_CONTRACT:
    case NOTIFICATION_CONTRACT_OWNERSHIP:
      store.dispatch('market/updateContractFromNotification', data);
      store.dispatch('market/updateRelatedContractFromNotification', data);
      store.dispatch('browse/updateContractFromNotification', data);
      store.dispatch('browse/updateGainsLossContractFromNotification', data);
      store.dispatch('browse/updatePredictedTrendingContractsFromNotification', data);
      store.dispatch('browse/updateMarketWidgetsContractsFromNotification', data);
      store.dispatch('dashboard/updateMySharesContractFromNotification', data);
      store.dispatch('search/updateContractFromNotification', data);
      store.dispatch('watchlist/updateWatchlistContractFromNotification', data);
      store.dispatch('popup/updateTradePopupOptionsContractsFromNotification', data);
      store.dispatch('market/updateMapOverviewDataFromNotification', data);
      store.dispatch('market/updateMapOverviewDataSenateFromNotification', data);
      // store.dispatch('browse/updateStoryMapFromContractNotification', data);
      break;
    case NOTIFICATION_SITE_STATUS:
      store.dispatch('app/setServerError', false);
      store.dispatch('app/setMaintenance', {
        isMaintenanceEnabled: data.IsMaintenance,
        isTradingEnabled: !data.IsTradingSuspended,
        maintenanceMessage: data.MaintenanceMessage,
        suspendedMessage: data.TradingSuspendedMessage,
        systemMessage: data.SystemMessage,
      });
      break;
    case NOTIFICATION_SHARES_TRADED:
      store.dispatch('notifications/add', {
        method,
        type: updateType,
        data: {
          contentJson: data,
          notificationType: NOTIFICATION_MATCHED,
        },
      });
      updateOrResetCounters();
      break;
    case NOTIFICATION_TRADER_LEVEL_CHANGED:
      store.dispatch('notifications/add', {
        method,
        type: updateType,
        data: {
          contentJson: data,
          notificationType: NOTIFICATION_LEVEL,
        },
      });
      updateOrResetCounters();
      break;
    case NOTIFICATION_PWMB_DEPOSIT_SHOW_CONFIRMATION:
      store.dispatch('wallet/setBankTransferStep', {
        step: data.PrefilledAmountToDeposit ? STEPS.CONFIRM : STEPS.AMOUNT_CONFIRM,
        stepData: reduce(data, (result, value, key) => {
          result[camelCase(key)] = value;
          return result;
        }, {}),
      });
      break;
    case NOTIFICATION_PWMB_SHOW_ERROR:
      store.dispatch('wallet/setBankTransferStep', {
        step: STEPS.FINISHED_FAILED,
        stepData: {
          error: data.MessageForUser,
        },
      });
      break;
    case NOTIFICATION_PWMB_DEPOSIT_FINISHED:
      store.dispatch('wallet/getBalance')
        .then(() => {
          return store.dispatch('wallet/setCurrentDeposit', {
            amount: data.TotalAmountDeposited,
            matched: data.AmountMatched,
            messageForUser: data.MessageForUser,
            confirmationNumber: data.PWMBCaptureId,
            type: 'bank-transfer',
            account: {
              accountStatus: 1,
              bankInfo: {
                bankIconUrl: data.BankIconURL,
                bankAccountDisplayName: data.BankAccountDisplayName,
                bankDisplayName: data.BankDisplayName,
              },
            },
          });
        })
        .then(() => {
          if (data.Status === FINISHED_STATUS.SUCCESS) {
            store.dispatch('wallet/setBankTransferStep', {
              step: STEPS.FINISHED_SUCCESS,
              stepData: {},
            });
          } else if (data.Status === FINISHED_STATUS.FAILED) {
            store.dispatch('wallet/setBankTransferStep', {
              step: STEPS.FINISHED_FAILED,
              stepData: {
                error: data.MessageForUser,
              },
            });
          } else if (data.Status === FINISHED_STATUS.EXPIRED) {
            store.dispatch('wallet/setBankTransferStep', {
              step: STEPS.FINISHED_EXPIRED,
              stepData: {
                pwmbAuthorizationId: data.PWMBAuthorizationId,
                error: data.MessageForUser,
              },
            });
          }
        });
      break;
    case NOTIFICATION_TRADE_CONFIRMED:
      store.dispatch('market/incrReloadHistoryToggle');
      store.dispatch('notifications/add', {
        method,
        type: updateType,
        data,
      });
      break;
    default: {
      const payload = {
        method,
        type: updateType,
        data,
      };
      store.dispatch('notifications/add', payload);
    }
  }
};

const KEY_MESSAGING_STATE = 'messagingState';
const MESSAGING_STATES = {
  OPT_IN: 'opt-in',
  OPT_OUT: 'opt-out',
  NO_DECISION: 'no-decision',
};
export default {
  initialisedFirebaseAnonymous: false,
  initialisedFirebase: false,
  initialisedSignalr: false,
  latestApiToken: null,
  init() {
    this.initFirebase();
    this.initSignalr();
  },
  initFirebase(apiToken = null) {
    firebaseClient.connect(apiToken, { onUpdate: addToStore });

    if (apiToken) {
      this.initialisedFirebase = true;
    } else {
      this.initialisedFirebaseAnonymous = true;
    }
  },
  initSignalr(apiToken = null) {
    if (!apiToken && !process.env.ANONYMOUS_SIGNALR) {
      return;
    }
    signalRClient.createConnection({ apiToken, onUpdate: addToStore });
    this.initialisedSignalr = true;
  },
  authorize(apiToken) {
    // reinitialise if it was switched off for anonymous users
    if (!this.initialisedFirebase) {
      log.info('firebase::', 'authorize::', 'init::');
      this.initFirebase(apiToken);
    }
    if (!this.initialisedSignalr) {
      log.info('socket::', 'authorize::', 'init::');
      this.initSignalr(apiToken);
      this.latestApiToken = apiToken;
    } else if (this.latestApiToken !== apiToken) {
      log.info('socket::', 'authorize::', 'reinitializetoken::');
      signalRClient.forceReconnect({ apiToken, onUpdate: addToStore });
      this.latestApiToken = apiToken;
    } else {
      log.info('socket::', 'authorize::', 'stale::');
    }
  },
  logout() {
    signalRClient.forceDisconnect();
    this.initialisedSignalr = false;
    // firebase is for anonymous users right now, no need to logout
    // if (window.firebase.apps.length > 0) {
    //   window.firebase.app().delete().then(() => {
    //   });
    // }
  },
  setupMessaging() {
    firebaseClient.setupMessaging();
  },
  forceDisconnect() {
    signalRClient.forceDisconnect();
  },
  shouldSetupMessaging() {
    if (!process.env.PUSH_NOTIFICATIONS_JOURNEY_ENABLED) return true;

    const state = ls.get(KEY_MESSAGING_STATE, MESSAGING_STATES.NO_DECISION);
    return state === MESSAGING_STATES.OPT_IN;
  },
  showMessagingJourney(isBuyTradeType) {
    if (!process.env.PUSH_NOTIFICATIONS_JOURNEY_ENABLED || !isBuyTradeType) return;

    const state = ls.get(KEY_MESSAGING_STATE, MESSAGING_STATES.NO_DECISION);
    if (state !== MESSAGING_STATES.NO_DECISION) return;

    setTimeout(() => {
      store.dispatch('popup/setCurrentSmallPopup', MODALS.SMALL_MESSAGING_JOURNEY);
    }, 4000);
  },
  optOutMessaging() {
    ls.set(KEY_MESSAGING_STATE, MESSAGING_STATES.OPT_OUT);
  },
  optInMessaging() {
    ls.set(KEY_MESSAGING_STATE, MESSAGING_STATES.OPT_IN);
  },
};



// WEBPACK FOOTER //
// ./src/services/notifications/proxy.js